import React from "react";
import tw from "twin.macro";
import { Button } from "~/features/ui/button";
import { Link } from "~/features/ui/link";
import { Headline } from "~/features/ui/headline";
import { IconButton } from "~/features/ui/icon-button";
import Plus from "~/public/icons/plus.svg";
import ArrowRight from "~/public/icons/arrow-right.svg";

type Props = {
  headline?: string;
  text?: string;
  url?: string;
  newWindow?: boolean;
  linkText?: string;
  media?: React.ReactNode;
  tagsPosition?: "body" | "media-overlay";
  tags?: React.ReactNode;
  /** Metadata row rendered above the headline, alongside media-overlay tags. */
  meta?: React.ReactNode;
  /** Tints the media's plus control on card hover, as the media-grid cards do. */
  highlightIconOnHover?: boolean;
  hasPlusIcon?: boolean;
  size: "regular" | "large";
  spacing?: "compact" | "regular";
};

export function EditorialFeaturedCard(props: Props) {
  const {
    headline,
    text,
    url,
    newWindow,
    linkText,
    media,
    tagsPosition = "media-overlay",
    tags,
    meta,
    size,
    spacing = "regular",
    hasPlusIcon = true,
    highlightIconOnHover,
  } = props;

  return (
    <Link href={url} newWindow={newWindow} className="group">
      <div css={[tw`flex flex-col h-full gap-24 lg:flex-row`, spacing === "compact" ? tw`lg:gap-40` : tw`lg:gap-80`]}>
        <div css={[tw`w-full h-full`, size === "large" && tw`lg:w-8/12`]}>
          <div tw="relative h-full block">
            {tags && tagsPosition === "media-overlay" && <div tw="absolute right-sm top-sm z-10">{tags}</div>}
            {!Boolean(linkText) && hasPlusIcon && (
              <div
                css={[
                  tw`absolute z-10 bottom-0 right-0`,
                  highlightIconOnHover &&
                    tw`[&>*]:transition-colors [&>*]:duration-250 can-hover:group-hover:[&>*]:!bg-blue-100`,
                ]}
              >
                <IconButton as="div" icon={{ component: Plus }} />
              </div>
            )}
            <div tw="w-full h-full">{media}</div>
          </div>
        </div>
        <div css={[tw`w-full flex flex-col`, size === "large" && tw`lg:w-4/12`]}>
          {tags && (tagsPosition === "body" || !media) && tags}
          {meta}
          {headline && (
            <Headline
              type="h2"
              tw="text-mobile-subtitle-30-normal mt-16 lg:(text-mobile-title-20-normal)"
              text={headline}
            />
          )}
          {text && (
            <div tw="text-mobile-body-20-normal mt-16">
              <p>{text}</p>
            </div>
          )}
          {url && Boolean(linkText) && (
            <div css={[tw`pt-40`, spacing !== "compact" && tw`mt-auto`]}>
              <Button as="div" icon={ArrowRight} variant="underline">
                {linkText}
              </Button>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
